import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyBrand, readCachedBrand, isValidBrandDoc,
  DEFAULT_KICKER, DEFAULT_PRODUCT_NAME,
  type BrandDocument, type BrandMode,
} from './brand';
import { useTheme, type ThemeMode } from '../theme/ThemeProvider';
import { APP_ENV } from '../lib/appEnv';

/**
 * BrandProvider (docs/17 B1 + B3) — resolves which brand renders and injects its
 * tokens. Source order, best signal first:
 *
 *   1. the host brand resolved from `window.location.hostname` (B3) — but ONLY
 *      when the resolver actually returned a brand document for that host
 *   2. the tenant brand delivered on /me (pushed in by BrandSync, below) — post-auth
 *   3. the brand cached from the last visit (instant flash prevention, host-keyed)
 *   4. Steward default (compiled-in CSS — no injection at all)
 *
 * NO QUERY PARAMETER APPEARS IN THAT CHAIN (steward-app#664, #791). `?brand=<name>`
 * — a compiled-in preset that outranked every other signal and persisted per browser
 * — is retired outright, and `?brandHost=` is refused in prod (see `resolveBrandHost`).
 * Every remaining input is a signal the firm actually owns: the hostname it was served
 * from, or the tenant brand /me returns for the viewer's own workspace.
 *
 * The host (2) OUTRANKS /me (3). That REVERSES the original rule (steward-app#755,
 * docs/17 §3 decision note 2026-08-23), under which /me always corrected whatever
 * the host had guessed, so a firm-A user visiting firm-B's subdomain ended up
 * firm-A branded once signed in. The reason for the reversal: a branded hostname
 * is a promise to whoever loads it. The firm's name is in the URL, so the firm's
 * brand belongs on the page — for its own clients, for a client of another firm
 * following a link, and for a signed-in viewer whose own tenant has no brand at
 * all. That last case is the one that prompted the change: such a viewer used to
 * get the bare Steward default ON the firm's own hostname. The narrower variant
 * ("host wins unless the viewer's own tenant has a brand") was considered and
 * rejected — see the decision note for both that and the known cost, which is
 * that a firm-A client following a link to firm-B's host wears firm-B's chrome
 * around their own data until they navigate away. Data, permissions, and the
 * active workspace are untouched by any of this; only presentation changes.
 *
 * ONLY AN ACTUAL BRAND DOCUMENT from the host outranks /me. `hostBrand` is
 * three-valued, and the other two values must both fall through to the rest of
 * the chain or the canonical host regresses to unbranded for signed-in users:
 * `null` = the resolver answered "no brand for this host" (what
 * `app[.env].steward.crossmarc.com` always answers), `undefined` = not resolved
 * yet, or the fetch failed. Those two are NOT interchangeable further down the
 * chain, though: below /me, a resolved `null` still beats the flash cache (it is
 * the resolver's answer; the cache is only a guess at it) and clears it, while
 * `undefined` leaves the cache alone. See `candidateBrand` for why that matters.
 *
 * Mode clamping: if the active brand restricts `modes`, a stored mode outside
 * the set RENDERS as the brand's first allowed mode, without being written back to
 * storage (#757). Brand supplies token *values*, mode picks *which set* renders.
 */

interface BrandContextValue {
  /** The active brand document, or null when the Steward default renders. */
  brand: BrandDocument | null;
  /** "Steward" or the brand's product name — for copy and titles. */
  productName: string;
  /** Short mark ("Steward" / "RIPL") — sidebar wordmark, "Ask <name>". */
  shortName: string;
  /** Eyebrow line above the wordmark; null = suppressed (firm brands). */
  kicker: string | null;
  /** True when a firm brand is active (endorser/legal lines recede). */
  isWhiteLabel: boolean;
  /** True once the brand in THIS context value is authoritative, so a surface that prints
   *  the product's name or filters on `isWhiteLabel` (the Announcements page,
   *  steward-app#2612) can hold until it reads true. It is set in the same commit as
   *  `brand` (a layout effect next to the one that sets the applied brand, batched into
   *  one render), so it never leads `brand`. Rules: with a host to resolve, the host
   *  resolver must have answered (a document, or "no brand for this host"), or have
   *  FAILED while /me has answered (offline or a blip must not hold a signed-in viewer
   *  forever); with no host, /me must have answered. `/me` alone does not settle a host
   *  that is still resolving, because a resolved host document outranks it. Never means
   *  the brand APPLIED cleanly: a malformed document still settles and falls back to
   *  the default. */
  settled: boolean;
  /** Theme modes the brand permits. */
  allowedModes: BrandMode[];
  /** BrandSync pushes the /me-delivered brand here (null = tenant has none). */
  setServerBrand: (doc: BrandDocument | null) => void;
}

const ALL_MODES: BrandMode[] = ['light', 'dark', 'system'];

const BrandContext = createContext<BrandContextValue>({
  brand: null,
  productName: DEFAULT_PRODUCT_NAME,
  shortName: DEFAULT_PRODUCT_NAME,
  kicker: DEFAULT_KICKER,
  isWhiteLabel: false,
  // A bare context (no provider, e.g. a test) has nothing to wait for.
  settled: true,
  allowedModes: ALL_MODES,
  setServerBrand: () => {},
});

export function useBrand() {
  return useContext(BrandContext);
}

/**
 * Which hostname to resolve the pre-auth brand for. Defaults to the real page host
 * (`window.location.hostname`); a `?brandHost=<host>` query override lets us drive a
 * firm's brand from the shared host for demos + verification BEFORE the wildcard
 * subdomain DNS exists. The value only ever becomes a public brand lookup — no auth,
 * no data beyond the firm's own public brand.
 *
 * PROD REFUSES THE OVERRIDE UNCONDITIONALLY, ON EVERY HOST (steward-app#791). The
 * façade's `reservedHosts()` (services/steward-app-api/src/handlers/branding.ts) keys
 * on the `host` QUERY PARAM, not on the host that actually served the page — so
 * `?brandHost=` let a link ask the façade about a DIFFERENT host while riding on a
 * real, TLS-valid Steward origin, painting that firm's brand onto a page the visitor
 * has every reason to trust. Only the SPA knows which host actually served it, so the
 * refusal has to live client-side.
 *
 * This SUPERSEDES the narrower guard of steward-app#621, which refused only when the
 * serving host appeared in a hand-kept `RESERVED_SHARED_HOSTS` set mirroring the
 * façade's list. That rule was strictly weaker in two ways: every prod host NOT in the
 * set (a future front door, a firm's own subdomain, anything added later) still
 * honored the override, and the set had to be kept in step with the façade by review,
 * so a drifting list silently narrowed the guard. The rule here needs no list and has
 * no drift mode: in prod there is no host on which the override is honored. Nothing in
 * prod needs one — a firm's brand reaches prod through its own hostname (B3) or
 * through the tenant brand /me returns (BrandSync), both signals the firm owns.
 *
 * Dev, stage, and local keep the override working unconditionally: it is the
 * documented pre-DNS verification path (docs/25 §Security checklist 7), and those
 * environments have no real families to spoof. Gating on the ENV rather than on the
 * host is the load-bearing part, and is deliberate — the verification path demos from
 * exactly the shared front doors (`localhost`, `app.dev.steward.crossmarc.com`) that a
 * host-based rule would have to refuse.
 *
 * PERSISTENCE ON REFUSAL: a refused override falls through with `isOverride: false`,
 * so the page behaves as an ordinary visit to its own host — including persisting that
 * host's resolved brand to the host-keyed flash cache (`applyBrand(brand, true)`
 * below), unlike an honored override, which is always inject-only. That is correct
 * rather than merely tolerable: the cache is keyed to the host that actually served
 * the page, and what gets written is whatever the façade says THAT host resolves to,
 * byte for byte what a visit with no query string would have written. The refused
 * parameter contributes nothing to it.
 */
function resolveBrandHost(): { host: string; isOverride: boolean } {
  try {
    const override = new URLSearchParams(window.location.search).get('brandHost');
    if (override && APP_ENV !== 'prod') {
      return { host: override, isOverride: true };
    }
    return { host: window.location.hostname, isOverride: false };
  } catch {
    return { host: '', isOverride: false };
  }
}

/**
 * Pre-auth host → brand fetch (B3). Calls the façade's public, unauthenticated
 * `/branding/resolve` (which fronts appcore + is fail-closed on entitlement).
 * Returns a validated brand, or `null` for "no brand → Steward default". THROWS on
 * a network/HTTP failure so the caller can keep the cached/default guess rather than
 * force the default on a transient blip.
 */
async function fetchHostBrand(host: string): Promise<BrandDocument | null> {
  if (!host) return null;
  const base = import.meta.env.VITE_API_BASE_URL;
  const res = await fetch(`${base}/branding/resolve?host=${encodeURIComponent(host)}`, {
    headers: { 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) throw new Error(`branding resolve failed: ${res.status}`);
  const { branding } = (await res.json()) as { branding: unknown };
  return isValidBrandDoc(branding) ? branding : null;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const { userMode, setBrandClamp } = useTheme();
  // Read once. Deliberately a useState initializer, not a useMemo over
  // `readCachedBrand()` called every render: useMemo's memoization is
  // documented as a performance hint React may discard, not a correctness
  // guarantee, and this value used to be the input to a downstream effect
  // (below) whose termination depended on it NOT changing identity between
  // renders with unchanged deps. Reading once makes that structurally
  // impossible to violate rather than relying on useMemo to keep holding.
  const [cachedBrand] = useState<BrandDocument | null>(readCachedBrand);
  // The host to resolve pre-auth + whether it came from the `?brandHost=` demo
  // override (computed once). An override session never persists to the flash cache
  // (see below), so previewing firm A from the shared host can't poison it.
  const [{ host: brandHost, isOverride }] = useState(resolveBrandHost);
  // undefined = /me hasn't answered yet (fall back to host/cache);
  // null = answered "no brand" (clear any stale cache and render default).
  const [serverBrand, setServerBrand] = useState<BrandDocument | null | undefined>(undefined);
  // Host resolution (B3), fetched pre-auth but authoritative at any point after
  // (see the header). undefined = not resolved yet (use the cached guess for an
  // instant paint); null = resolver answered "no brand" for this host; doc = the
  // firm's brand. Left undefined on a fetch failure (keep cache/default).
  const [hostBrand, setHostBrand] = useState<BrandDocument | null | undefined>(undefined);
  // True once the host fetch threw. Carries no brand fact (the cached guess stays), but
  // it stops a failed resolver from holding `settled` false for the whole session.
  const [hostFailed, setHostFailed] = useState(false);

  // Resolve the host brand once on mount (fired pre-auth — /me has not answered yet
  // when this goes out, so serverBrand is still undefined here). Skipped only if we
  // somehow have no host.
  //
  // This request and /me RACE, and the race is settled by the precedence in
  // `candidateBrand` below (a resolved host DOCUMENT outranks serverBrand), never by
  // ordering here: whichever lands first, the settled value on a branded host is the
  // host's brand. The race is therefore only visible as a brief flash, and only for a
  // signed-in viewer on a branded host whose own tenant brand differs — if /me lands
  // first they see their own brand for a moment before the host's replaces it.
  // DELIBERATELY NOT HELD: gating the first paint on this fetch would cost every load
  // (including the unauthenticated one this resolver exists to serve) a network
  // round-trip of blank or default-branded page, which is the exact flash the
  // host-keyed cache and the index.html boot script exist to avoid. A bounded,
  // self-correcting flicker for one signed-in case is the cheaper of the two.
  useEffect(() => {
    if (!brandHost) return;
    let cancelled = false;
    fetchHostBrand(brandHost)
      .then((doc) => { if (!cancelled) setHostBrand(doc); })
      .catch(() => { if (!cancelled) setHostFailed(true); /* keep the cached/default guess */ });
    return () => { cancelled = true; };
    // Run once on mount; brandHost is captured once (a useState initializer).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every operand here is a stable state reference (see `cachedBrand` above
  // for why that one, specifically, is read once rather than called live) —
  // so this expression's VALUE is already stable across renders that don't
  // change one of serverBrand/hostBrand/cachedBrand, with no memo needed to
  // keep it that way.
  //
  // `hostBrand ?? …` is the three-valued test the header describes, and
  // `??` (not `||`) is the point: it stops ONLY on a non-nullish value, so a resolved
  // brand DOCUMENT wins outright while both `null` and `undefined` fall through.
  // Neither may outrank serverBrand — `null` winning there would unbrand every
  // signed-in user on the canonical host, and `undefined` winning would let one
  // transient resolver blip unbrand a signed-in session.
  //
  // The tail is written LONGHAND rather than closing as `serverBrand !== undefined ?
  // serverBrand : cachedBrand`, because those two fall-through values are NOT
  // interchangeable below /me, and collapsing them loses a behavior the canonical
  // host depends on. `hostResolved && hostBrand === null` means the resolver has
  // ANSWERED, "no brand for this host" — a fresher fact than the flash cache, which
  // is only ever a guess AT that answer from the last visit. So pre-auth it must
  // beat the cache and resolve to null, which sends `applyBrand(null, persist)` on
  // to clearBrandCache() and self-heals a stale entry. Without this branch, a firm
  // brand cached under `app.steward.crossmarc.com` (written by a signed-in firm-A
  // user on that host, since the cache is host-keyed, not tenant-keyed) would render
  // on the canonical login page for every later visitor of that browser, forever,
  // with nothing left to clear it — the shared-front-door spoofing surface that the
  // prod `?brandHost=` refusal (steward-app#791, and #621 before it) exists to keep
  // firm branding off. `undefined` (not
  // resolved yet, or the fetch threw) carries no such fact and correctly leaves the
  // cached guess in place: that is the flash-free first paint.
  const hostResolved = hostBrand !== undefined;
  // What the brand WOULD be authoritative as of this render. Not handed to consumers
  // directly: see `settled` below, which trails it into the same commit as appliedBrand.
  const settledNow = brandHost
    ? hostResolved || (hostFailed && serverBrand !== undefined)
    : serverBrand !== undefined;
  const candidateBrand = hostBrand
    ?? (serverBrand !== undefined
      ? serverBrand
      : (hostResolved ? null : cachedBrand));

  // The brand actually handed to context/consumers — held directly in state
  // (set only from inside the effect below, after applyBrand() has either
  // succeeded or been substituted with null), NOT derived live from
  // `candidateBrand`. That distinction matters: an earlier version of this fix
  // derived `brand` from a `applyFailed` boolean (`applyFailed ? null :
  // candidateBrand`), which meant that for the ONE render where a new,
  // ultimately-failing `candidateBrand` first appears, `brand` (and anything
  // computed from it, like `allowedModes` below) was still the raw,
  // NOT-yet-validated candidate — because the catch block's state update
  // lands one render later, but OTHER effects in this component (the mode
  // clamp below) run in the SAME commit, using that same not-yet-validated
  // value. For a brand with a restricted `modes` set, that let a doc which
  // ultimately throws inside applyBrand() still clamp the user's theme-mode
  // for a brand that was never actually applied (it no longer persists, #757). Holding the
  // applied brand directly closes that: no downstream consumer can observe a
  // candidate before it has round-tripped through applyBrand().
  //
  // Starts `null`, so React's OWN first render (before any effect has run)
  // computes an unbranded `brand` even when the initial candidate is valid.
  // The unbranded FIRST PAINT never happens — the effect below is a
  // `useLayoutEffect`, which React flushes, including any state update it
  // makes, before the browser paints, so a correcting re-render settles in
  // the same paint frame — but this unbranded commit's own PASSIVE effects
  // (an ordinary `useEffect`, anywhere in the tree, including the mode clamp
  // a few lines down) DO still run against it: React flushes a commit's
  // passive effects before running the layout-effect-triggered re-render,
  // not after. That is safe here ONLY because `null` is a PERMISSIVE value
  // for the one thing that reads it that way (`allowedModes` falls back to
  // every mode), not because the value is somehow invisible. Do not seed
  // this state from `candidateBrand` to "skip" the extra render — that
  // would silently reopen exactly the #707 hole this file exists to close,
  // by handing an unvalidated candidate to that same passive-effect window.
  //
  // A plain `useEffect` here was tried first and rejected on grounds this
  // reasoning already rules out: it runs after paint, so EVERY load, not
  // just a failing one, would visibly flash the Steward default before the
  // real brand appeared. On RECOVERY specifically (a failed candidate
  // replaced by a later good one), the same pre-paint settling means
  // observers only ever see the PREVIOUS applied brand or the NEW one —
  // never the Steward default flashing in between — which is the better of
  // the two directions: a firm's users should never see a competitor's or
  // the bare-default chrome mid-session.
  const [appliedBrand, setAppliedBrand] = useState<BrandDocument | null>(null);

  useLayoutEffect(() => {
    // Defense in depth: a malformed brand doc (bad cache, future schema drift)
    // must degrade to unbranded, never crash the whole app on first paint.
    // Override sessions inject-only (persist=false): a `?brandHost=` demo must not
    // write the persistent, host-keyed flash cache tagged to the real page host.
    try {
      applyBrand(candidateBrand, !isOverride);
      setAppliedBrand(candidateBrand);
    } catch (err) {
      // steward-app#707: keep this identifying-but-minimal — brand docs can carry
      // operator-authored strings (productName, wordmark text), so no full dump.
      console.warn(
        '[brand] applyBrand failed; falling back to unbranded (React state included, not just CSS)',
        {
          brandVersion: candidateBrand?.brandVersion ?? null,
          productName: candidateBrand?.productName ?? null,
          host: brandHost || null,
          isOverride,
        },
        err,
      );
      // Strip whatever CSS/font/favicon may already be applied. Whether that
      // also clears the persisted flash cache is conditional, refining (not
      // reversing) the original "never clear on a render failure" call:
      // clearing is right ONLY when the doc that just failed IS the cached
      // one (reference equality against `cachedBrand`, the once-read state
      // above — never a deep compare, and never true for an override
      // session, which must have no persistent side effects at all). A
      // transient failure on a server or host doc must not degrade the
      // next cold load's first paint by wiping a cache that was never the
      // problem — that's the case the original instruction protects, and it
      // still applies unconditionally here. But a CACHED doc that throws
      // deterministically IS the poison: nothing else will ever dislodge it,
      // so every future cold load would re-fail against the same doc
      // forever, and a user has no way to clear it themselves. Clearing in
      // that one case is a self-heal, not a degradation.
      applyBrand(null, !isOverride && candidateBrand === cachedBrand);
      setAppliedBrand(null);
    }
  }, [candidateBrand, isOverride, brandHost, cachedBrand]);

  // `settled` is published from a layout effect declared AFTER the one above, so the
  // two setStates land in one batched render: the context can never show settled=true
  // beside an `appliedBrand` older than the answer that settled it.
  const [settled, setSettled] = useState(false);
  useLayoutEffect(() => { setSettled(settledNow); }, [settledNow]);

  // On a failed apply, unbrand REACT STATE too (steward-app#707) — otherwise the
  // CSS tokens are gone but BrandContext still hands consumers the firm's brand
  // doc, so the lockup/product name/title stay branded while the palette
  // reverts to default. Coherent-unbranded beats half-branded.
  const brand = appliedBrand;

  // Clamp the theme mode to what the brand permits (e.g. light-only firms).
  const allowedModes = brand?.modes?.length ? brand.modes : ALL_MODES;
  // The clamp changes what renders, never what is stored (#757): the viewer's own choice
  // survives and comes back when no brand restricts it (another host, a `?brandHost=`
  // override, a firm that supplies both palettes).
  useEffect(() => {
    setBrandClamp(allowedModes.includes(userMode as BrandMode) ? null : (allowedModes[0] as ThemeMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clamp only on brand/mode change
  }, [allowedModes.join(','), userMode]);

  const value = useMemo<BrandContextValue>(() => ({
    brand,
    productName: brand?.productName ?? DEFAULT_PRODUCT_NAME,
    shortName: brand?.wordmark?.text ?? brand?.productName ?? DEFAULT_PRODUCT_NAME,
    kicker: brand ? (brand.wordmark?.kicker ?? null) : DEFAULT_KICKER,
    isWhiteLabel: !!brand,
    settled,
    allowedModes,
    setServerBrand: (doc: BrandDocument | null) => setServerBrand(doc),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allowedModes derives from brand
  }), [brand, settled]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/**
 * Mounted inside WorkspaceProvider (which owns /me): pushes the active
 * tenant's brand into BrandProvider whenever the active workspace's tenant —
 * or the /me payload — changes. Kept as a separate tiny component so
 * BrandProvider can wrap the entire tree (Login included) without a
 * circular dependency on workspace state.
 */
export function BrandSync({ branding, activeTenantId, meResolved }: {
  branding: Record<string, BrandDocument> | undefined;
  activeTenantId: string | null;
  /** True once /me has loaded. Distinguishes "still loading" (keep the cached
   *  guess) from "loaded, but no active tenant" (clear any lingering brand). */
  meResolved: boolean;
}) {
  const { setServerBrand } = useBrand();
  useEffect(() => {
    if (!meResolved) return; // /me still pending — keep the cached pre-auth guess
    if (!activeTenantId) {
      // Resolved with no active tenant (member-less, or last workspace deleted):
      // fall back to the default so a firm brand can't linger on the picker.
      setServerBrand(null);
      return;
    }
    setServerBrand(branding?.[activeTenantId] ?? null);
  }, [branding, activeTenantId, meResolved, setServerBrand]);
  return null;
}
